// Fachada ligera del embed (~2 KB): expone window.MIA nada más cargar el
// script y difiere la app real (React, markdown, gráficos…) hasta el primer
// open(). mount() solo guarda opciones y precalienta el bundle en idle.

/** Sin jti: 'popup' ofrece login OAuth de FarmaCloud; 'none' (defecto) exige jti. */
export type MiaAuthFallback = 'popup' | 'none'

export type MiaEmbedOptions = {
  /** Prefijo o URL del BFF MIA. Si se omite, usa la horneada en build. */
  apiBaseUrl?: string
  /** JTI de la sesión FarmaCloud para autorización silenciosa. */
  jti?: string
  /** Muestra datos técnicos de tools/thread en la UI. También disponible con #debug=1. */
  debug?: boolean
  /** Idioma de la UI. Si se omite: <html lang> y por último "es". */
  language?: string
  /** Abre el modal justo después de montar. */
  open?: boolean
  /** Fallback de auth cuando no se pasa jti. Defecto: 'none'. */
  authFallback?: MiaAuthFallback
}

export type MiaOpenOptions = {
  jti?: string
  debug?: boolean
  language?: string
  authFallback?: MiaAuthFallback
}

export type MiaInlineOptions = {
  /** Contenedor del host (elemento o selector). Debe tener altura explícita. */
  container: string | HTMLElement
  jti?: string
  debug?: boolean
  language?: string
  authFallback?: MiaAuthFallback
}

export type MiaEmbedApi = {
  mount: (options?: MiaEmbedOptions) => void
  open: (options?: MiaOpenOptions) => void
  close: () => void
  toggle: () => void
  destroy: () => void
  isOpen: () => boolean
  /** Monta MIA rellenando un contenedor del host (webapp), sin modal. */
  mountInline: (options: MiaInlineOptions) => void
  unmountInline: () => void
}

declare global {
  interface Window {
    MIA?: MiaEmbedApi
  }
}

let appApi: MiaEmbedApi | null = null
let appPromise: Promise<MiaEmbedApi> | null = null
let mountedOptions: MiaEmbedOptions = {}
// Estado que pide el host antes de que cargue la app; una vez cargada, la
// fuente de verdad es la propia app (el usuario puede cerrar desde la UI).
let desiredOpen = false
let pendingOpen: MiaOpenOptions | undefined
let pendingInline: MiaInlineOptions | undefined

function loadApp(): Promise<MiaEmbedApi> {
  appPromise ??= import('./embed-app').then((mod) => {
    appApi = mod.MIA
    appApi.mount(mountedOptions)
    if (pendingInline) {
      appApi.mountInline(pendingInline)
      pendingInline = undefined
    }
    if (desiredOpen) appApi.open(pendingOpen)
    return appApi
  })
  return appPromise
}

function prewarm(): void {
  if (appPromise) return
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => void loadApp())
  } else {
    setTimeout(() => void loadApp(), 1500)
  }
}

const facade: MiaEmbedApi = {
  mount(options = {}) {
    mountedOptions = { ...mountedOptions, ...options }
    desiredOpen ||= !!options.open
    if (appApi) {
      appApi.mount(mountedOptions)
      if (options.open) appApi.open()
      return
    }
    if (desiredOpen) void loadApp()
    else prewarm()
  },
  open(options) {
    if (appApi) {
      appApi.open(options)
      return
    }
    desiredOpen = true
    pendingOpen = options
    void loadApp()
  },
  close() {
    desiredOpen = false
    appApi?.close()
  },
  toggle() {
    if (appApi) appApi.toggle()
    else facade.open(pendingOpen)
  },
  destroy() {
    desiredOpen = false
    pendingOpen = undefined
    pendingInline = undefined
    appApi?.destroy()
  },
  isOpen: () => appApi?.isOpen() ?? false,
  mountInline(options) {
    if (appApi) {
      appApi.mountInline(options)
      return
    }
    pendingInline = options
    void loadApp()
  },
  unmountInline() {
    pendingInline = undefined
    appApi?.unmountInline()
  }
}

window.MIA = facade

// Auto-montaje opcional desde la propia etiqueta:
// <script src=".../mia.js" data-auto-mount data-api-base-url="…" data-jti="…"></script>
const script = document.currentScript
if (script?.hasAttribute('data-auto-mount')) {
  const authFallback = script.getAttribute('data-auth-fallback')
  const debug = script.getAttribute('data-debug')
  facade.mount({
    apiBaseUrl: script.getAttribute('data-api-base-url') ?? undefined,
    jti: script.getAttribute('data-jti') ?? undefined,
    debug: debug === null ? undefined : debug !== 'false',
    language: script.getAttribute('data-language') ?? undefined,
    open: script.hasAttribute('data-open'),
    authFallback: authFallback === 'popup' || authFallback === 'none' ? authFallback : undefined
  })
}
